import React, { useEffect, useRef, useState } from 'react'
import {
  Maximize as MaximizeIcon,
  Minimize as MinimizeIcon,
} from 'react-feather'
import L from 'leaflet'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Box } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'

import helpers from 'helpers'

import theme from 'theme'

import constants from 'constants/index'
import useStyles from './styles'

const ReactLeaflet = ({ markers = [] }) => {
  const [fullscreen, setFullscreen] = useState(false)

  const classes = useStyles()
  const { i18n } = useTranslation()

  const mapRef = useRef(null)
  const btnRef = useRef(null)

  const { getProportionalRadius, translateBaseMapLabels } = helpers.leaflet
  const { BASE_MAP_STYLE_URL, BASE_MAP_WORKER_URL } = constants.leaflet

  useEffect(() => {
    if (mapRef.current && !mapRef.current._leaflet_id) {
      const map = L.map(mapRef.current, {
        center: [20, 0],
        zoom: 2,
      })

      // carregado sob demanda para o maplibre (~800 KB) não entrar no bundle principal
      Promise.all([
        import('maplibre-gl'),
        import('@maplibre/maplibre-gl-leaflet'),
      ]).then(([{ default: maplibregl }]) => {
        maplibregl.setWorkerUrl(BASE_MAP_WORKER_URL)

        // o crédito exigido pelo OpenFreeMap vem do próprio estilo; o plugin ignora `attribution`
        const maplibreMap = L.maplibreGL({ style: BASE_MAP_STYLE_URL })
          .addTo(map)
          .getMaplibreMap()

        // sem cookie nem ?lng=, o i18next não preenche `language` e a tela usa o fallback
        maplibreMap.once('load', () =>
          translateBaseMapLabels(
            maplibreMap,
            i18n.language || i18n.options?.fallbackLng?.[0],
          ),
        )
      })

      if (!isEmpty(markers)) {
        const minQ = Math.min(...markers.map((m) => m.quantity))
        const maxQ = Math.max(...markers.map((m) => m.quantity))

        markers.forEach((marker) => {
          const radius = getProportionalRadius(marker.quantity, minQ, maxQ)
          const circle = L.circleMarker(marker.coords, {
            radius,
            color: theme.palette.primary.hover,
            fillColor: theme.palette.primary.hover,
            fillOpacity: 0.5,
          }).addTo(map)
          circle.bindTooltip(`${marker.city} (${marker.quantity})`)
        })
      }
    }

    if (btnRef.current) {
      btnRef.current.onclick = handleFullscreen
      // como nos controles do Leaflet: clique no botão não arrasta nem dá zoom no mapa
      L.DomEvent.disableClickPropagation(btnRef.current)
    }
    document.addEventListener('fullscreenchange', handleFsChange)

    return () => {
      if (mapRef.current && mapRef.current._leaflet_id)
        mapRef.current._leaflet_id = null

      document.removeEventListener('fullscreenchange', handleFsChange)
    }
  }, [])

  const handleFsChange = () => setFullscreen(!!document.fullscreenElement)

  const handleFullscreen = (event) => {
    event.preventDefault()
    if (!document.fullscreenElement) mapRef.current.requestFullscreen()
    else document.exitFullscreen()
  }

  const FullScreenIcon = fullscreen ? MinimizeIcon : MaximizeIcon

  return (
    <Box
      position="relative"
      ref={mapRef}
      className={classes.mapContainer}
      id="shared-map"
    >
      {/* classes do próprio Leaflet, para o botão ficar idêntico ao controle de zoom */}
      <Box className="leaflet-top leaflet-right">
        <Box className="leaflet-bar leaflet-control">
          <a
            ref={btnRef}
            href="#fullscreen"
            role="button"
            className={classes.button}
            title={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            aria-label={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          >
            <FullScreenIcon size={18} strokeWidth={2.5} />
          </a>
        </Box>
      </Box>
    </Box>
  )
}

const BaseLeaflet = ({ isLoading = false, markers = [] }) => {
  return isLoading ? (
    <Skeleton width="100%" height="100%" variant="rect" />
  ) : (
    <ReactLeaflet markers={markers} />
  )
}

export default BaseLeaflet
